import { createInertiaApp, router } from '@inertiajs/react'
import { AppShell } from '@/components/app-shell'
import { initializeTheme } from '@/hooks/use-appearance'
import { captureEvent, disableCapturing } from '@/lib/analytics'

initializeTheme()

// PostHog capture is manual (see app/javascript/lib/analytics.ts and the
// init config in application.html.erb) because this is an SPA: auth state
// can flip from signed-out to signed-in mid-session via a client-side
// redirect (e.g. after Register.tsx's form.post), without a full page
// reload that would re-run the server-side `!user_signed_in?` guard. `nav`
// is only present in shared props once the user has an authenticated
// membership (see ApplicationController#inertia_share), so it's the one
// reliable per-navigation signal for whether PostHog should be capturing.
router.on('navigate', event => {
  const props = event.detail.page.props as { nav?: unknown }
  if (props.nav) {
    disableCapturing()
  } else {
    captureEvent('$pageview')
  }
})

void createInertiaApp({
  pages: "../pages",

  // Applied to every page by default; a page can opt out by setting its own
  // `Component.layout`. Must return the component itself, not a
  // page-wrapping function - when there's no per-page `.layout`, Inertia
  // treats this return value as a plain component and does the
  // createElement(Component, props, children) wrapping itself.
  // AppShell wraps OrgLayout, which no-ops (renders children unwrapped) when
  // there's no `nav` shared prop - the case for any page reachable before
  // sign-in (no org membership yet) - so this doesn't need a page-name
  // exclusion list. AppShell also mounts the flash toaster, outside that
  // no-op so pre-auth pages still get their messages.
  layout: () => AppShell,

  // Fallback for every page that doesn't set its own via <Head title="...">
  // or <Head><title>. Keeps title ownership entirely with Inertia's head
  // manager — a page-level <Head><title> and a static layout <title> tag
  // both being present would double up in the raw (pre-hydration) HTML,
  // which is exactly the surface non-JS crawlers read.
  title: (title) => title || "Donzy",

  strictMode: true,

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: "brackets" }
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
